import { useQuery } from '@tanstack/react-query'
import { ApiError, type Account } from '@greencandle/api-client'
import { api } from '../api'
import { Money, Percent } from '../components/Figure'

export function AccountsPage() {
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
  })

  if (accounts.isPending) {
    return <LoadingPage />
  }

  if (accounts.isError) {
    return (
      <div className="notice error" role="alert">
        {accounts.error instanceof ApiError
          ? (accounts.error.problem.detail ?? accounts.error.message)
          : 'Could not reach the API.'}
      </div>
    )
  }

  if (accounts.data.length === 0) {
    return (
      <>
        <h1 className="page-title">Accounts</h1>
        <p className="page-subtitle">Nothing here yet.</p>
        <div className="notice">
          No accounts exist. Create one with <code>POST /api/accounts</code> — the import wizard
          that will do this from the UI is FR-H3.
        </div>
      </>
    )
  }

  return (
    <>
      <h1 className="page-title">Accounts</h1>
      <p className="page-subtitle">
        {accounts.data.length} account{accounts.data.length === 1 ? '' : 's'}
      </p>

      <div className="grid">
        {accounts.data.map((account) => (
          <AccountCard key={account.id} account={account} />
        ))}
      </div>
    </>
  )
}

/**
 * One account, with what it is currently worth.
 *
 * The total is the sum the server already computed for the allocation report rather than a sum
 * taken here: FR-E4 excludes holdings it could not value and says how many, and adding the
 * figures up in the browser would quietly discard that distinction.
 */
function AccountCard({ account }: { account: Account }) {
  const allocation = useQuery({
    queryKey: ['allocation', account.id, 'ASSET_CLASS'],
    queryFn: () => api.allocation({ dimension: 'ASSET_CLASS', accountId: account.id }),
  })

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <strong style={{ fontSize: 15 }}>{account.name}</strong>
        <span className="pill">{account.kind}</span>
      </div>

      <div className="stat-label">Market value</div>
      <div className="stat-value">
        {allocation.isPending ? (
          <span className="skeleton" style={{ display: 'block', width: '60%', height: 28 }} />
        ) : allocation.isError ? (
          <span className="unknown" title={String(allocation.error)}>
            not available
          </span>
        ) : (
          <Money value={allocation.data.total} currency={allocation.data.currency} />
        )}
      </div>

      {allocation.data && allocation.data.buckets.length > 0 && (
        <table style={{ marginTop: 16 }}>
          <tbody>
            {allocation.data.buckets.map((bucket) => (
              <tr key={bucket.key}>
                <td style={{ paddingLeft: 0 }}>{bucket.key}</td>
                <td className="right">
                  <Money value={bucket.value} currency={allocation.data.currency} />
                </td>
                <td className="right muted" style={{ paddingRight: 0, width: 80 }}>
                  <Percent value={bucket.weightPercent} signed={false} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {allocation.data?.note && (
        <div className="notice warn" style={{ marginTop: 12 }}>
          {allocation.data.note}
        </div>
      )}

      <div className="faint" style={{ marginTop: 12, fontSize: 12 }}>
        Base {account.baseCurrency} · {account.costBasisMethod}
        {allocation.data && (
          <>
            {' '}
            · valued {allocation.data.holdingsValued} of {allocation.data.holdingsConsidered}
          </>
        )}
      </div>
    </div>
  )
}

function LoadingPage() {
  return (
    <>
      <h1 className="page-title">Accounts</h1>
      <div className="grid" style={{ marginTop: 24 }}>
        {[0, 1, 2].map((i) => (
          <div className="card" key={i}>
            <span className="skeleton" style={{ display: 'block', width: '50%' }} />
            <span
              className="skeleton"
              style={{ display: 'block', width: '70%', height: 28, marginTop: 16 }}
            />
          </div>
        ))}
      </div>
    </>
  )
}
